/**
 * Inline-summary extraction for notification bodies.
 *
 * Notification bodies are Markdown (see the server's `formatActionApprovalBody`):
 * a leading one-sentence summary — "**An agent** wants to merge [Name](url)." —
 * followed by detail lines (`- Entity id: 886`), the proposer's reason, and a
 * "[Review in Lobu](url)" link. The timeline card shows only the first block;
 * the detail belongs in the focused/peek view, not in a two-line clamp.
 */

/** Trailing review CTA — its own affordance in the card, never summary prose. */
const REVIEW_LINK_LINE = /^\[Review in Lobu\]\(.*\)$/;

/** `- Label: value` detail lines that follow the summary sentence. */
const DETAIL_LINE = /^[-*]\s/;

/**
 * First meaningful block of a notification body: the summary sentence, with the
 * detail list, reason paragraph, and review link dropped. Markdown is preserved
 * (bold, links) for the caller to render — this only chooses *what* to show.
 *
 * Returns null when nothing renderable remains, so callers can skip the node
 * rather than render an empty paragraph.
 */
export function notificationSummaryMarkdown(
	body: string | null | undefined,
): string | null {
	if (!body) return null;

	const summary: string[] = [];
	for (const raw of body.split("\n")) {
		const line = raw.trim();
		// A blank line ends the summary block only once we have prose; leading
		// blanks are skipped so a body that opens with a newline still resolves.
		if (line === "") {
			if (summary.length > 0) break;
			continue;
		}
		if (DETAIL_LINE.test(line) || REVIEW_LINK_LINE.test(line)) break;
		summary.push(line);
	}

	// Bodies wrap the summary sentence across lines; rejoin so it reads as one
	// paragraph and the Markdown link syntax stays intact.
	const text = summary.join(" ").trim();
	if (text !== "") return text;

	// No leading prose. Approval bodies always have some, but an agent digest can
	// open straight into a bullet list — returning null there would blank the card
	// body entirely, which is worse than the untruncated text it used to show.
	// Fall back to the leading lines (minus the review CTA); the card clamps them.
	const fallback = body
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line !== "" && !REVIEW_LINK_LINE.test(line))
		.join(" ")
		.trim();
	return fallback === "" ? null : fallback;
}
